import React from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Paper,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function RoadkillCitiesConfig({
  gameConfig,
  setGameConfig,
  loading,
}) {
  const { isViewer } = getUserPermissions();

  // Get current cities with fallback to default cities
  const getCurrentCities = () => {
    if (gameConfig.cities && gameConfig.cities.length > 0) {
      return gameConfig.cities;
    } else {
      return [
        {
          id: 1,
          name: "New York",
          entry: 2
        },
      ];
    }
  };

  const handleCityChange = (index, field, value) => {
    const currentCities = [...getCurrentCities()];
    currentCities[index] = {
      ...currentCities[index],
      [field]: field === 'name' ? value : parseFloat(value) || 0,
    };

    setGameConfig({ ...gameConfig, cities: currentCities });
  };

  const handleAddCity = () => {
    const currentCities = getCurrentCities();
    const maxId = Math.max(...currentCities.map(city => city.id), 0);
    const newCities = [
      ...currentCities,
      {
        id: maxId + 1,
        name: `City ${currentCities.length + 1}`,
        entry: 10,
      },
    ];

    setGameConfig({ ...gameConfig, cities: newCities });
  };

  const handleDeleteCity = (index) => {
    const currentCities = getCurrentCities();
    if (currentCities.length <= 1) return;

    const newCities = currentCities.filter((_, i) => i !== index);

    setGameConfig({ ...gameConfig, cities: newCities });
  };

  return (
    <Box sx={{ mt: 2, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Roadkill Cities Configuration
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 3 }}
      >
        Configure roadkill cities with their entry costs
      </Typography>

      <Grid container spacing={3}>
        {getCurrentCities().map((city, index) => (
          <Grid item xs={12} key={`city-${city.id}`}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                "&:hover": {
                  boxShadow: 3,
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  City {index + 1}
                </Typography>
                {!isViewer && (
                  <IconButton
                    onClick={() => handleDeleteCity(index)}
                    disabled={loading || getCurrentCities().length <= 1}
                    size="small"
                    sx={{
                      color:
                        getCurrentCities().length <= 1 ? "#ccc" : "#d32f2f",
                      "&:hover": {
                        backgroundColor:
                          getCurrentCities().length <= 1
                            ? "transparent"
                            : "#ffcdd2",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="City Name"
                    value={city.name}
                    onChange={(e) =>
                      handleCityChange(index, "name", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#1976d2",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Entry"
                    type="number"
                    value={city.entry}
                    onChange={(e) =>
                      handleCityChange(index, "entry", e.target.value)
                    }
                    variant="outlined"
                    disabled={loading}
                    inputProps={{ min: 0, step: 1 }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#ffffff",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "#4caf50",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCity}
          disabled={loading}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
          }}
        >
          Add New City
        </Button>
      </Box>
    </Box>
  );
}
